import { hasActiveSampleSession } from "./sampleAccess";

export const GUEST_USAGE_KEY = "venaqqi_guest_recommendation_usage_v1";
export const GUEST_USAGE_SCHEMA_VERSION = 1;
export const GUEST_RECOMMENDATION_LIMIT = 2;
export const GUEST_PERIOD_MS = 7 * 24 * 60 * 60 * 1000;
export const SAMPLE_RECOMMENDATION_ALLOWANCE = 99;

const newGuestId = () => `guest_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

export const createGuestUsage = (guestId = newGuestId()) => ({
  schemaVersion: GUEST_USAGE_SCHEMA_VERSION,
  guestId,
  periodStartedAt: null,
  successfulPlaceRecommendationCount: 0,
  lastSuccessfulRecommendationAt: null,
  recommendationIds: [],
});

export const normalizeGuestUsage = (value, now = Date.now()) => {
  const base = createGuestUsage(value?.guestId);
  const started = Number(value?.periodStartedAt);
  if (!started || now - started >= GUEST_PERIOD_MS || now < started) return base;
  const ids = Array.isArray(value?.recommendationIds) ? [...new Set(value.recommendationIds.map(String))].slice(0, 2) : [];
  return {
    ...base,
    periodStartedAt: started,
    successfulPlaceRecommendationCount: Math.min(GUEST_RECOMMENDATION_LIMIT, Math.max(0, Number(value?.successfulPlaceRecommendationCount) || 0)),
    lastSuccessfulRecommendationAt: Number(value?.lastSuccessfulRecommendationAt) || null,
    recommendationIds: ids,
  };
};

export const readGuestUsage = (storage = window.localStorage, now = Date.now()) => {
  let parsed;
  try { parsed = JSON.parse(storage.getItem(GUEST_USAGE_KEY) || "null"); } catch { parsed = null; }
  const usage = normalizeGuestUsage(parsed, now);
  try { storage.setItem(GUEST_USAGE_KEY, JSON.stringify(usage)); } catch { /* storage unavailable */ }
  return usage;
};

export const writeGuestUsage = (usage, storage = window.localStorage) => {
  const normalized = normalizeGuestUsage(usage);
  try { storage.setItem(GUEST_USAGE_KEY, JSON.stringify(normalized)); } catch { /* storage unavailable */ }
  return normalized;
};

export const remainingRecommendations = usage => hasActiveSampleSession()
  ? SAMPLE_RECOMMENDATION_ALLOWANCE
  : Math.max(0, GUEST_RECOMMENDATION_LIMIT - Math.min(GUEST_RECOMMENDATION_LIMIT, Math.max(0, Number(usage?.successfulPlaceRecommendationCount) || 0)));

export const recordSuccessfulRecommendations = (usage, placeIds, now = Date.now()) => {
  const current = normalizeGuestUsage(usage, now);
  if (hasActiveSampleSession(undefined, now)) return current;
  const fresh = [...new Set((placeIds || []).map(String))].filter(id => !current.recommendationIds.includes(id));
  const accepted = fresh.slice(0, remainingRecommendations(current));
  if (!accepted.length) return current;
  return {
    ...current,
    periodStartedAt: current.periodStartedAt || now,
    successfulPlaceRecommendationCount: current.successfulPlaceRecommendationCount + accepted.length,
    lastSuccessfulRecommendationAt: now,
    recommendationIds: [...current.recommendationIds, ...accepted],
  };
};

export const remainingTimeLabel = (usage, now = Date.now()) => {
  if (hasActiveSampleSession(undefined, now)) return null;
  const current = normalizeGuestUsage(usage, now);
  if (!current.periodStartedAt) return null;
  const milliseconds = Math.max(0, GUEST_PERIOD_MS - (now - current.periodStartedAt));
  if (milliseconds < 24 * 60 * 60 * 1000) return "menos de 1 día";
  const days = Math.ceil(milliseconds / (24 * 60 * 60 * 1000));
  return `${days} día${days === 1 ? "" : "s"}`;
};

export const firstRecommendationNotice = (usage, now = Date.now()) => hasActiveSampleSession(undefined, now)
  ? ""
  : `Como invitado puedes recibir 2 recomendaciones cada 7 días. Ya utilizaste 1 y te queda 1. Podrás renovar tus recomendaciones en ${remainingTimeLabel(usage, now)}.`;

export const exhaustedRecommendationNotice = (usage, now = Date.now()) => hasActiveSampleSession(undefined, now)
  ? ""
  : `Ya utilizaste tus 2 recomendaciones de este periodo. Podrás recibir nuevas recomendaciones en ${remainingTimeLabel(usage, now)}. Regístrate para continuar con libertad de uso.`;

const normalize = text => String(text || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
export const isMultiPlaceRequest = text => hasActiveSampleSession()
  ? false
  : /\b(lista|ranking|top\s*\d*|varias? (?:opciones|alternativas)|compara(?:r|cion)?|itinerario|\d+ (?:lugares|restaurantes|opciones))\b/i.test(normalize(text));
export const isRecommendationRequest = text => /\b(recomiend|sugier|donde (?:comer|cenar|desayunar)|que (?:como|comer|lugar)|otra (?:opcion|alternativa)|sorprendeme|algo (?:rapido|rico|barato))\b/i.test(normalize(text));

// Primary contract: the model emits catalog IDs in a hidden footer. The fallback only
// accepts canonical bold catalog names when the assistant explicitly recommends them.
export const extractRecommendedPlaces = (reply, places = []) => {
  const text = String(reply || "");
  const marker = text.match(/\[RECOMMENDATION_IDS:([^\]]*)\]/i);
  const byId = new Map(places.map(place => [String(place.id), place]));
  if (marker) {
    const ids = [...new Set(marker[1].split(",").map(id => id.trim()).filter(Boolean))];
    return ids.map(id => byId.get(id)).filter(Boolean);
  }
  if (!/\b(?:te recomiendo|mi recomendaci[oó]n|ve a|prueba|elige|opci[oó]n principal)\b/i.test(text)) return [];
  const bold = [...text.matchAll(/\*\*([^*]+)\*\*/g)].map(match => normalize(match[1]));
  return places.filter(place => bold.includes(normalize(place.name.split("–")[0].trim())));
};

export const sanitizeRecommendationReply = (reply, places, remaining) => {
  const detected = extractRecommendedPlaces(reply, places);
  const clean = String(reply || "").replace(/\s*\[RECOMMENDATION_IDS:[^\]]*\]\s*/gi, "").trim();
  if (detected.length <= remaining) return { reply: clean, places: detected, truncated: false };
  // Do not expose a partial generated ranking: replace it locally with the allowed,
  // catalog-backed primary recommendations rather than spending a regeneration call.
  const allowed = detected.slice(0, remaining);
  return {
    reply: allowed.map(place => `Te recomiendo **${place.name}** — ${place.tipo} en ${place.location}.`).join("\n"),
    places: allowed,
    truncated: true,
  };
};

export const guestLimitMessage = "Ya utilizaste tus 2 recomendaciones de este periodo. Regístrate para continuar con libertad de uso.";
export const guestListMessage = "Para recibir listas o más opciones de lugares, regístrate y continúa con libertad de uso.";
